import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Chip, Divider, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Paper, Dialog, DialogContent,
    DialogTitle, IconButton, CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import axiosInstance from '../../lib/axios';

// Map each dashboard "flag" to the payload BedStatus/Flag value your API expects.
// ⚠️ 'P' (Partial) and 'B' (Booked) are placeholders — replace with your real codes.
const FLAG_TO_BEDSTATUS = {
    O: 'O',   // Occupied
    P: 'P', // Partial
    A: 'A',   // Available
    B: 'B',   // Booked
};

const OccupancyDetails = ({ open, onClose, flags = [], title = 'Occupancy Details', propId }) => {
    const [details, setDetails] = useState([]);
    const [loading, setLoading] = useState(false);

    const fetchOccupancyDetails = async () => {
        if (!flags.length) {
            setDetails([]);
            return;
        }
        try {
            setLoading(true);
            const responses = await Promise.all(
                flags.map(f =>
                    axiosInstance.post('TrnBooking/GetBedStatusRetrive', {
                        PropId: propId,
                        RoomBedId: 1,
                        MonthNm: '',
                        MonthId: 1,
                        YearNm: 2027,
                        BedStatus: FLAG_TO_BEDSTATUS[f] ?? f,
                        Flag: FLAG_TO_BEDSTATUS[f] ?? f
                    })
                )
            );

            const merged = responses
                .filter(r => r?.data?.Status === 0)
                .flatMap(r => r.data.Data?.BedStatusRetrive || []);

            setDetails(merged);
        } catch (error) {
            setDetails([]);
            toast.error('Error while fetching details.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            fetchOccupancyDetails();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, JSON.stringify(flags), propId]);

    const records = details;
    const total = records.length;
    const occupied = records.filter(x => x.BedStatus === 'O').length;
    const available = records.filter(x => x.BedStatus === 'A').length;
    const partial = records.filter(x => x.BedStatus === 'P').length;

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('en-GB', {
            day: '2-digit', month: '2-digit', year: 'numeric'
        });
    };

    const getStatus = (status) => {
        const map = {
            P: { label: 'Partial', color: '#d97706', bg: '#fff7ed' },
            A: { label: 'Available', color: '#059669', bg: '#ecfdf5' },
            O: { label: 'Occupied', color: '#dc2626', bg: '#fef2f2' },
            B: { label: 'Booked', color: '#2563eb', bg: '#eff6ff' },
            C: { label: 'Confirmed', color: 'Green', bg: '#eff6ff' },
        };
        return map[status] || { label: status || 'Unknown', color: '#64748b', bg: '#f8fafc' };
    };

    // Only show the stat boxes relevant to the flags this dialog was opened with.
    const statBoxes = [
        ['Total', total, '#475569', '#f8fafc'],
        flags.includes('P') && ['Partial', partial, '#d97706', '#fff7ed'],
        flags.includes('O') && ['Occupied', occupied, '#dc2626', '#fef2f2'],
        flags.includes('A') && ['Available', available, '#059669', '#ecfdf5'],
    ].filter(Boolean);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1.5, py: 1 }}>
                <Box>
                    <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: '#1e293b' }}>
                        {title}
                    </Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: '#64748b', mt: 0.3 }}>
                        Bed-wise booking and occupancy information
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                        label={`${total} Beds`}
                        size="small"
                        sx={{
                            height: 28,
                            px: 0.5,
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            color: '#2563eb',
                            background: '#eff6ff',
                            border: '1px solid #dbeafe',
                            borderRadius: '8px'
                        }}
                    />
                    <IconButton onClick={onClose} size="small" color='error' >
                        <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ pt: 0 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${statBoxes.length}, 1fr)`, gap: 1.2, mb: 1 }}>
                    {statBoxes.map(([label, value, color, bg]) => (
                        <Box key={label} sx={{ p: 1, borderRadius: 1.5, background: bg, border: `1px solid ${color}22` }}>
                            <Typography sx={{ fontSize: '0.65rem', color: '#64748b' }}>{label}</Typography>
                            <Typography sx={{ fontSize: '1.15rem', fontWeight: 700, color, mt: 0.2 }}>{value}</Typography>
                        </Box>
                    ))}
                </Box>

                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 1.5, maxHeight: 400 }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                {['Bed', 'Booking No.', 'Tenure', 'Booking Date', 'Checkout', 'Month', 'Status'].map(head => (
                                    <TableCell key={head} sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#475569', py: 1, background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                        {head}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                                        <CircularProgress size={22} />
                                    </TableCell>
                                </TableRow>
                            ) : (
                                <>
                                    {records.map((row) => {
                                        const status = getStatus(row.BedStatus);
                                        return (
                                            <TableRow key={row.BedCalendarId} hover>
                                                <TableCell sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#1e293b' }}>{row.RoombedName || '-'}</TableCell>
                                                <TableCell sx={{ fontSize: '0.72rem', color: '#334155', fontWeight: 600 }}>{row.BookNo || '-'}</TableCell>
                                                <TableCell sx={{ fontSize: '0.72rem', }}>{row.Tenure ? `${row.Tenure} mo` : '-'}</TableCell>
                                                <TableCell sx={{ fontSize: '0.72rem', }}>{formatDate(row.BookDtFrom)}</TableCell>
                                                <TableCell sx={{ fontSize: '0.72rem', }}>{formatDate(row.CheckoutDt)}</TableCell>
                                                <TableCell sx={{ fontSize: '0.72rem', }}>{row.MonthNm ? `${row.MonthNm} ${row.YearNm}` : '-'}</TableCell>
                                                <TableCell>
                                                    <Chip label={status.label} size="small" sx={{ height: 22, fontSize: '0.62rem', fontWeight: 700, color: status.color, background: status.bg, border: `1px solid ${status.color}30` }} />
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                    {!records.length && (
                                        <TableRow>
                                            <TableCell colSpan={7} align="center" sx={{ py: 5, color: '#94a3b8', fontSize: '0.75rem' }}>
                                                No occupancy details found
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>
        </Dialog>
    );
};

export default OccupancyDetails;